import React, { useCallback, useState } from 'react'
import { CardIcon, CardList, CardTitle, ListItem, OptionCard } from '../selling-option-card'
import Hammer from '../../../components/svg/hammer'
import PurchaseTag from '../../../components/svg/purchase-tag'
import { Button } from '../../../components/button'
import { useAuth } from '../../../contexts/auth'
import CashOffer from '../../../components/svg/cash-offer'
import { VIEWS as AUTH_VIEWS } from '../../auth/modals'
import EnterVinModal from './components/modals/enter-vin'
import { ListingType } from '../../../api-client/enums'
import FindVehicleModal, { VIEWS } from '../modals/find-vehicle'
import VinInstruction from '../modals/vin-instruction'
import { checkPermission } from '../../../utils/permissions'
import { permissions } from '../../../constants'
import NoticeAboutConciergeModal from './components/modals/notice-about-concierge'

const SellingOptions = () => {
  const [{ userDetails, isAuthenticated }, { setAuthModal }] = useAuth()
  const [enterVinModalOpen, setEnterVinModalOpen] = useState(false)
  const [listingType, setListingType] = useState(null)
  const [findVehicleModalOpen, setFindVehicleModalOpen] = useState(false)
  const [defaultModalView, setDefaultModalView] = useState(VIEWS.Result)
  const [vehicle, setVehicle] = useState(null)
  const [vinInstructionModalOpen, setVinInstructionModalOpen] = useState(false)
  const [noticeAboutConciergeModalOpen, setNoticeAboutConciergeModalOpen] = useState(false)

  const getCashOfferPermissions = () => {
    if (!isAuthenticated) return true
    return checkPermission(userDetails.permissions, [permissions.manageCashOffer])
  }

  const handleStartListing = (listingType) => {
    if (isAuthenticated) {
      setListingType(listingType)
      setNoticeAboutConciergeModalOpen(true)
    } else {
      setAuthModal({ open: true, view: AUTH_VIEWS.Login })
    }
  }

  const handleFindVehicleModalClose = useCallback(() => {
    setDefaultModalView(VIEWS.Result)
    setFindVehicleModalOpen(false)
  }, [])

  const handleEnterVinSuccess = useCallback((vehicle) => {
    setVehicle(vehicle)
    setEnterVinModalOpen(false)
    setDefaultModalView(VIEWS.Result)
    setFindVehicleModalOpen(true)
  }, [])

  const handleManualEntry = useCallback(() => {
    setEnterVinModalOpen(false)
    setDefaultModalView(VIEWS.ManualEntry)
    setFindVehicleModalOpen(true)
  }, [])

  const handleNotFound = useCallback(() => {
    setEnterVinModalOpen(false)
    setDefaultModalView(VIEWS.NotFound)
    setFindVehicleModalOpen(true)
  }, [])

  const handleAcceptConciergeCondition = () => {
    setNoticeAboutConciergeModalOpen(false)
    setEnterVinModalOpen(true)
  }

  return (
    <div className='root'>
      <h2>How do you want to sell your vehicle?</h2>
      <div className='options-wrapper'>
        {userDetails.verified && (
          <OptionCard className='options-card'>
            <CardTitle>Live Auction</CardTitle>
            <CardIcon>
              <Hammer />
            </CardIcon>
            <Button onClick={() => handleStartListing(ListingType.Auction)}>Get started</Button>
            <CardList>
              <ListItem pros>
                Pro: Puts multiple people competing to buy your car, always sure to get the most for
                your vehicle!
              </ListItem>
              <ListItem pros>
                Pro: Takes away the pressure of knowing how to price your vehicle accurately
              </ListItem>
              <ListItem pros={false}>
                Con: May take time to sell, depending on how many people are looking for a vehicle
                like yours
              </ListItem>
            </CardList>
          </OptionCard>
        )}

        {userDetails.verified && (
          <OptionCard className='options-card'>
            <CardTitle>Buy it Now Listing</CardTitle>
            <CardIcon>
              <PurchaseTag />
            </CardIcon>
            <Button onClick={() => handleStartListing(ListingType.BuyNow)}>Get started</Button>
            <CardList>
              <ListItem pros>
                Pro: Often quickest and easiest sale type, assuming you are priced right
              </ListItem>
              <ListItem pros>
                Pro: Lasts 14 days ensuring plenty of time for buyers to find your vehicle
              </ListItem>
              <ListItem pros={false}>
                Con: Offers and counter offers may be seen as "Haggling"
              </ListItem>
            </CardList>
          </OptionCard>
        )}

        {getCashOfferPermissions() && (
          <OptionCard className='options-card'>
            <CardTitle>Quick Cash Offer</CardTitle>
            <CardIcon>
              <CashOffer />
            </CardIcon>
            <Button onClick={() => handleStartListing(ListingType.CashOffer)}>Get started</Button>
            <CardList>
              <ListItem pros>
                Pro: We pay you cash for your vehicle, no need to wait to see if it sells in auction
                or to Buy it Now
              </ListItem>
              <ListItem pros>Pro: Offer is guaranteed and not dependent on a third party</ListItem>
              <ListItem pros={false}>
                Con: You may get less than a private buyer may pay you via Auction or Buy It Now
              </ListItem>
            </CardList>
          </OptionCard>
        )}
      </div>

      {enterVinModalOpen && (
        <EnterVinModal
          onSuccess={handleEnterVinSuccess}
          onClose={() => setEnterVinModalOpen(false)}
          onManualEntry={handleManualEntry}
          onNotFound={handleNotFound}
          onSeeInstructions={() => setVinInstructionModalOpen(true)}
        />
      )}

      {findVehicleModalOpen && (
        <FindVehicleModal
          listingType={listingType}
          defaultView={defaultModalView}
          vehicle={vehicle}
          onClose={handleFindVehicleModalClose}
        />
      )}

      {vinInstructionModalOpen && (
        <VinInstruction onClose={() => setVinInstructionModalOpen(false)} />
      )}

      {noticeAboutConciergeModalOpen && (
        <NoticeAboutConciergeModal
          onAccept={handleAcceptConciergeCondition}
          onClose={() => setNoticeAboutConciergeModalOpen(false)}
        />
      )}

      <style jsx>{`
        .root {
          h2 {
            margin: 0 0 2.1rem;
            text-align: center;
            font-size: 5.4rem;
          }

          .options-wrapper {
            display: flex;
            flex-direction: row;
            justify-content: center;

            :global(.options-card) {
              &:not(:last-child) {
                margin: 0 2.5rem 0 0;
              }
            }
          }
        }

        @media (max-width: 1024px) {
          .root {
            h2 {
              margin: 0 0 1.3rem;
              font-size: 2rem;
            }

            .options-wrapper {
              flex-direction: column;
              align-items: center;

              :global(.options-card) {
                &:not(:last-child) {
                  margin: 0 0 1.5rem 0;
                }
              }
            }
          }
        }
      `}</style>
    </div>
  )
}

export default SellingOptions
